<template>
  <div class="coupons-management">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-tag-multiple</v-icon>
                Coupon Codes Management
                <span class="page-inline-subtitle">Manage discount coupons for subscriptions</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn 
                  color="primary" 
                  @click="showCreateDialog = true"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Coupon
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Coupons Table -->
      <v-row>
        <v-col cols="12">
          <v-card class="coupons-table-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="filters-header-container">
                <div class="filters-single-row">
                  <!-- Title Section -->
                  <div class="table-title-section">
                    <h2 class="table-title">
                      <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                      All Coupons
                    </h2>
                    <div class="results-count" v-if="count > 0">
                      <div class="count-display">
                        <span class="count-number">{{ count }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Clean Filters Section -->
                  <div class="filters-section">
                    <v-text-field
                      v-model="form.search_keyword"
                      placeholder="Search coupons..."
                      prepend-inner-icon="mdi-magnify"
                      solo
                      dense
                      clearable
                      hide-details
                      class="filter-search clean-filter"
                      @click:clear="(form.search_keyword = null), (page = 1), getData()"
                      @keydown.enter="(page = 1), getData()"
                    ></v-text-field>
                    
                    <v-select
                      v-model="form.type"
                      :items="couponTypes"
                      placeholder="Type"
                      solo
                      dense
                      clearable
                      hide-details
                      class="filter-select clean-filter"
                      @input="(page = 1), getData()"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon small color="#64748b">mdi-percent</v-icon>
                      </template>
                    </v-select>
                    
                    <v-select
                      v-model="form.is_active"
                      :items="statuses"
                      placeholder="Status"
                      solo
                      dense
                      clearable
                      hide-details
                      class="filter-select clean-filter"
                      @input="(page = 1), getData()"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon small color="#64748b">mdi-check-circle-outline</v-icon>
                      </template>
                    </v-select>
                    
                    <div class="filter-actions">
                      <v-btn
                        @click="resetFilters()"
                        outlined
                        class="filter-reset-btn clean-btn"
                        small
                        color="#64748b"
                      >
                        <v-icon small left>mdi-refresh</v-icon>
                        Reset
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-title>

            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="coupons"
                :loading="isLoading"
                :server-items-length="count"
                :items-per-page="rowsPerPage"
                :page="page"
                @update:page="updatePage"
                class="modern-data-table"
                hide-default-footer
              >
              <template v-slot:[`item.sr_no`]="{ index }">
                <span class="serial-number">{{ (page - 1) * rowsPerPage + index + 1 }}</span>
              </template>

              <template v-slot:[`item.is_active`]="{ item }">
                <v-chip
                  :color="item.is_active ? 'green' : 'red'"
                  dark
                  small
                >
                  {{ item.is_active ? 'ACTIVE' : 'INACTIVE' }}
                </v-chip>
              </template>

              <template v-slot:[`item.coupon_info`]="{ item }">
                <div class="coupon-info">
                  <div class="coupon-code">
                    <v-icon class="mr-2" :color="getCouponColor(item.type)">{{ getCouponIcon(item.type) }}</v-icon>
                    <strong>{{ item.code }}</strong>
                  </div>
                  <div class="coupon-description">{{ item.description || 'No description' }}</div>
                </div>
              </template>

              <template v-slot:[`item.discount`]="{ item }">
                <div class="discount-info">
                  <div class="discount-value">
                    {{ item.type === 'percent' ? item.value + '%' : '$' + item.value }}
                    <span class="discount-type">{{ item.type === 'percent' ? 'OFF' : 'OFF' }}</span>
                  </div>
                  <div class="discount-max" v-if="item.max_discount && item.type === 'percent'">
                    Max: ${{ item.max_discount }}
                  </div>
                </div>
              </template>

              <template v-slot:[`item.usage`]="{ item }">
                <div class="usage-info">
                  <div class="usage-count">{{ item.used_count || 0 }} / {{ item.usage_limit || '∞' }}</div>
                  <v-progress-linear
                    v-if="item.usage_limit"
                    :value="(item.used_count || 0) / item.usage_limit * 100"
                    height="4"
                    color="primary"
                    background-color="grey lighten-3"
                    class="mt-1"
                  ></v-progress-linear>
                </div>
              </template>

              <template v-slot:[`item.validity`]="{ item }">
                <div class="validity-info">
                  <div class="validity-dates">
                    <div class="start-date">From: {{ formatDate(item.start_date) }}</div>
                    <div class="end-date" v-if="item.end_date">To: {{ formatDate(item.end_date) }}</div>
                    <div class="no-expiry" v-else>No Expiry</div>
                  </div>
                  <v-chip
                    :color="getValidityStatus(item).color"
                    dark
                    x-small
                    class="mt-1"
                  >
                    {{ getValidityStatus(item).text }}
                  </v-chip>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon
                  small
                  @click="editCoupon(item)"
                  color="primary"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click="deleteCoupon(item)"
                  color="error"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              </v-data-table>
              
              <!-- Custom Pagination -->
              <div class="modern-pagination pa-4">
                <v-pagination
                  v-model="page"
                  :length="paginationLength"
                  :total-visible="7"
                  color="primary"
                  @input="getData"
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Create/Edit Coupon Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="700px" persistent>
      <v-card class="coupon-dialog" elevation="24">
        <v-card-title class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-section">
              <h2 class="dialog-title">{{ editingCoupon ? 'Edit Coupon' : 'Create New Coupon' }}</h2>
              <p class="dialog-subtitle">{{ editingCoupon ? 'Update coupon information' : 'Create a new discount coupon' }}</p>
            </div>
            <v-btn 
              icon 
              @click="closeDialog"
              class="close-btn"
            >
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content pa-6">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="couponForm.code"
                outlined
                label="Coupon Code *"
                prepend-inner-icon="mdi-tag"
                :error-messages="couponErrors.code"
                class="minimal-input"
                dense
                hide-details="auto"
                :hint="'Example: SAVE20, WELCOME10'"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="couponForm.type"
                :items="couponTypes"
                outlined
                label="Discount Type *"
                prepend-inner-icon="mdi-percent"
                :error-messages="couponErrors.type"
                class="minimal-input"
                dense
                hide-details="auto"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="couponForm.value"
                outlined
                :label="couponForm.type === 'percent' ? 'Discount Percentage *' : 'Discount Amount *'"
                :prepend-inner-icon="couponForm.type === 'percent' ? 'mdi-percent' : 'mdi-currency-usd'"
                :error-messages="couponErrors.value"
                class="minimal-input"
                dense
                type="number"
                min="0"
                :max="couponForm.type === 'percent' ? 100 : undefined"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" v-if="couponForm.type === 'percent'">
              <v-text-field
                v-model="couponForm.max_discount"
                outlined
                label="Maximum Discount ($)"
                prepend-inner-icon="mdi-currency-usd"
                :error-messages="couponErrors.max_discount"
                class="minimal-input"
                dense
                type="number"
                min="0"
                hide-details="auto"
                hint="Leave empty for no limit"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="couponForm.description"
                outlined
                label="Description"
                prepend-inner-icon="mdi-text"
                :error-messages="couponErrors.description"
                class="minimal-input"
                dense
                rows="2"
                hide-details="auto"
              ></v-textarea>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="couponForm.start_date"
                outlined
                label="Start Date *"
                prepend-inner-icon="mdi-calendar"
                :error-messages="couponErrors.start_date"
                class="minimal-input"
                dense
                type="date"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="couponForm.end_date"
                outlined
                label="End Date"
                prepend-inner-icon="mdi-calendar"
                :error-messages="couponErrors.end_date"
                class="minimal-input"
                dense
                type="date"
                hide-details="auto"
                hint="Leave empty for no expiry"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="couponForm.usage_limit"
                outlined
                label="Usage Limit"
                prepend-inner-icon="mdi-counter"
                :error-messages="couponErrors.usage_limit"
                class="minimal-input"
                dense
                type="number"
                min="1"
                hide-details="auto"
                hint="Leave empty for unlimited use"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="couponForm.min_order_amount"
                outlined
                label="Minimum Order Amount ($)"
                prepend-inner-icon="mdi-currency-usd"
                :error-messages="couponErrors.min_order_amount"
                class="minimal-input"
                dense
                type="number"
                min="0"
                hide-details="auto"
                hint="Leave empty for no minimum"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="couponForm.is_active"
                inset
                color="primary"
                :label="`Status: ${couponForm.is_active ? 'Active' : 'Inactive'}`"
                class="minimal-switch"
                hide-details
              ></v-switch>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="dialog-actions pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeDialog"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="saveCoupon"
            :loading="isSaving"
            class="save-btn"
          >
            {{ editingCoupon ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "CouponsPage",
  data() {
    return {
      form: {
        search_keyword: null,
        type: null,
        is_active: null,
      },
      coupons: [],
      headers: [
        { text: 'Sr. No.', value: 'sr_no', sortable: false, width: '80px' },
        { text: 'Status', value: 'is_active', sortable: true, width: '120px' },
        { text: 'Coupon Info', value: 'coupon_info', sortable: false, width: '250px' },
        { text: 'Discount', value: 'discount', sortable: false, width: '150px' },
        { text: 'Usage', value: 'usage', sortable: false, width: '150px' },
        { text: 'Validity', value: 'validity', sortable: false, width: '180px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' },
      ],
      couponTypes: [
        { text: 'All Types', value: null },
        { text: 'Percentage', value: 'percent' },
        { text: 'Fixed Amount', value: 'fixed' }
      ],
      statuses: [
        { text: 'All', value: null },
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      page: 1,
      rowsPerPage: 15,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      showCreateDialog: false,
      editingCoupon: null,
      isSaving: false,
      couponForm: {
        code: '',
        type: 'percent',
        value: '',
        max_discount: '',
        description: '',
        start_date: new Date().toISOString().substr(0, 10),
        end_date: '',
        usage_limit: '',
        min_order_amount: '',
        is_active: true,
      },
      couponErrors: {},
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      this.form.search_keyword = this.form.search_keyword || "";
      this.form.type = this.form.type || "";
      this.form.is_active = this.form.is_active !== null ? this.form.is_active : "";
      
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&search_keyword=" +
        this.form.search_keyword +
        "&type=" +
        this.form.type +
        "&is_active=" +
        this.form.is_active;

      try {
        const response = await axios.get(`coupons?${query}`);
        this.coupons = response.data.data;
        this.count = response.data.count;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.error('Error fetching coupons:', error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
        // Fallback to sample coupon data
        this.coupons = [
          {
            id: 1,
            code: 'WELCOME20',
            type: 'percent',
            value: 20,
            max_discount: 100,
            description: 'Welcome discount for new customers',
            start_date: '2024-01-01',
            end_date: '2024-12-31',
            usage_limit: 100,
            used_count: 15,
            is_active: true,
            created_at: new Date().toISOString()
          },
          {
            id: 2,
            code: 'SAVE50',
            type: 'fixed',
            value: 50,
            max_discount: null,
            description: 'Fixed $50 discount on any plan',
            start_date: '2024-01-01',
            end_date: '2024-06-30',
            usage_limit: 50,
            used_count: 8,
            is_active: true,
            created_at: new Date().toISOString()
          },
          {
            id: 3,
            code: 'FIRST30',
            type: 'percent',
            value: 30,
            max_discount: 150,
            description: 'First-time customer special offer',
            start_date: '2024-03-01',
            end_date: '2024-09-30',
            usage_limit: 200,
            used_count: 45,
            is_active: true,
            created_at: new Date().toISOString()
          },
          {
            id: 4,
            code: 'STUDENT15',
            type: 'percent',
            value: 15,
            max_discount: 75,
            description: 'Student discount for educational purposes',
            start_date: '2024-01-01',
            end_date: null,
            usage_limit: null,
            used_count: 22,
            is_active: true,
            created_at: new Date().toISOString()
          },
          {
            id: 5,
            code: 'ENTERPRISE100',
            type: 'fixed',
            value: 100,
            max_discount: null,
            description: 'Enterprise plan discount',
            start_date: '2024-01-01',
            end_date: '2024-12-31',
            usage_limit: 25,
            used_count: 3,
            is_active: true,
            created_at: new Date().toISOString()
          },
          {
            id: 6,
            code: 'EARLY25',
            type: 'percent',
            value: 25,
            max_discount: 125,
            description: 'Early adopter discount',
            start_date: '2024-01-01',
            end_date: '2024-05-31',
            usage_limit: 75,
            used_count: 12,
            is_active: false,
            created_at: new Date().toISOString()
          }
        ];
        this.count = this.coupons.length;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      }
      this.isLoading = false;
    },
    updatePage(newPage) {
      this.page = newPage;
      this.getData();
    },
    resetFilters() {
      this.form.search_keyword = null;
      this.form.type = null;
      this.form.is_active = null;
      this.page = 1;
      this.getData();
    },
    editCoupon(coupon) {
      this.editingCoupon = coupon;
      this.couponForm = {
        code: coupon.code,
        type: coupon.type,
        value: coupon.value,
        max_discount: coupon.max_discount || '',
        description: coupon.description || '',
        start_date: coupon.start_date,
        end_date: coupon.end_date || '',
        usage_limit: coupon.usage_limit || '',
        min_order_amount: coupon.min_order_amount || '',
        is_active: coupon.is_active,
      };
      this.showCreateDialog = true;
    },
    closeDialog() {
      this.showCreateDialog = false;
      this.editingCoupon = null;
      this.couponForm = {
        code: '',
        type: 'percent',
        value: '',
        max_discount: '',
        description: '',
        start_date: new Date().toISOString().substr(0, 10),
        end_date: '',
        usage_limit: '',
        min_order_amount: '',
        is_active: true,
      };
      this.couponErrors = {};
    },
    async saveCoupon() {
      try {
        this.isSaving = true;
        this.couponErrors = {};
        
        if (this.editingCoupon) {
          await axios.put(`/coupons/${this.editingCoupon.id}`, this.couponForm);
        } else {
          await axios.post('/coupons', this.couponForm);
        }
        
        this.closeDialog();
        this.getData();
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          this.couponErrors = error.response.data.errors;
        }
        console.error('Error saving coupon:', error);
      }
      this.isSaving = false;
    },
    async deleteCoupon(coupon) {
      if (confirm(`Are you sure you want to delete coupon "${coupon.code}"? This action cannot be undone.`)) {
        try {
          await axios.delete(`/coupons/${coupon.id}`);
          this.getData();
        } catch (error) {
          console.error('Error deleting coupon:', error);
        }
      }
    },
    getCouponIcon(type) {
      return type === 'percent' ? 'mdi-percent' : 'mdi-currency-usd';
    },
    getCouponColor(type) {
      return type === 'percent' ? '#10b981' : '#667eea';
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString();
    },
    getValidityStatus(coupon) {
      const now = new Date();
      const startDate = new Date(coupon.start_date);
      const endDate = coupon.end_date ? new Date(coupon.end_date) : null;
      
      if (now < startDate) {
        return { text: 'Not Started', color: 'warning' };
      } else if (endDate && now > endDate) {
        return { text: 'Expired', color: 'error' };
      } else if (coupon.usage_limit && coupon.used_count >= coupon.usage_limit) {
        return { text: 'Used Up', color: 'error' };
      } else {
        return { text: 'Valid', color: 'success' };
      }
    },
  },
};
</script>

<style scoped>
.coupons-management {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  justify-self: flex-end;
  margin-left: auto;
}

/* Coupons Table Card */
.coupons-table-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Filters Header Container */
.filters-header-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.filters-single-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 24px;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.results-count {
  display: flex;
  align-items: center;
}

.count-display {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 0 16px;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.count-number {
  font-size: 18px;
  font-weight: 700;
  color: white;
  line-height: 1;
}

/* Filters Section */
.filters-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
}

/* Clean Filter Styling */
.clean-filter {
  margin: 0 !important;
}

.clean-filter >>> .v-input__control {
  min-height: 40px !important;
}

.clean-filter >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1px solid #d1d5db !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
  padding: 0 16px !important;
  margin: 0 !important;
}

.clean-filter >>> .v-input__control .v-input__slot:before,
.clean-filter >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.clean-filter >>> .v-input__control .v-input__slot:hover {
  border-color: #9ca3af !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.clean-filter >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #64748b !important;
  box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.15) !important;
}

/* Remove any fieldset or additional borders */
.clean-filter >>> fieldset {
  display: none !important;
}

/* Search Filter - Wider */
.filter-search.clean-filter {
  width: 280px !important;
  min-width: 280px !important;
}

/* Select Filters */
.filter-select.clean-filter {
  width: 160px !important;
  min-width: 160px !important;
}

/* Filter Actions */
.filter-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
}

/* Clean Buttons */
.clean-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  height: 40px !important;
  min-width: auto !important;
  transition: all 0.2s ease;
  font-size: 13px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.clean-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

/* Reset Button */
.filter-reset-btn.clean-btn {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  color: #64748b !important;
}

.filter-reset-btn.clean-btn:hover {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
}

/* Data Table */
.modern-data-table {
  background: transparent !important;
}

.modern-data-table >>> .v-data-table__wrapper {
  background: white;
}

.modern-data-table >>> thead tr th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
  padding: 16px 12px !important;
}

.modern-data-table >>> thead tr th:last-child {
  border-right: none !important;
}

.modern-data-table >>> tbody tr {
  transition: all 0.3s ease;
}

.modern-data-table >>> tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
}

.modern-data-table >>> tbody tr td {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
  border-right: 1px solid rgba(102, 126, 234, 0.1) !important;
}

.modern-data-table >>> tbody tr td:last-child {
  border-right: none !important;
}

/* Serial Number Styling */
.serial-number {
  font-weight: 600;
  font-size: 14px;
  color: #64748b;
  background: rgba(102, 126, 234, 0.08);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
  min-width: 24px;
  text-align: center;
}

/* Coupon Info Styling */
.coupon-info {
  display: flex;
  flex-direction: column;
}

.coupon-code {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.coupon-description {
  font-size: 12px;
  color: #64748b;
}

/* Discount Info Styling */
.discount-info {
  text-align: center;
}

.discount-value {
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
}

.discount-type {
  font-size: 10px;
  font-weight: 500;
  color: #64748b;
}

.discount-max {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

/* Usage Info Styling */
.usage-info {
  text-align: center;
}

.usage-count {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

/* Validity Info Styling */
.validity-info {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.validity-dates {
  font-size: 11px;
  color: #64748b;
  text-align: center;
  margin-bottom: 4px;
}

.start-date,
.end-date,
.no-expiry {
  margin-bottom: 2px;
}

/* Pagination */
.modern-pagination {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  display: flex;
  justify-content: center;
}

.modern-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  font-weight: 500;
}

.modern-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

/* Action Buttons in Table */
.v-btn--icon {
  border-radius: 8px !important;
}

/* Chips in table */
.v-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

/* Header Button Styling */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
  min-width: auto !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

.modern-btn:before {
  background-color: transparent;
}

/* Dialog Styling */
.coupon-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 24px !important;
  border-radius: 0 !important;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.dialog-title-section {
  flex: 1;
}

.dialog-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.dialog-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.dialog-content {
  background: #f8f9fa;
}

/* Minimal Input Styling for Dialog */
.minimal-input {
  margin-bottom: 8px;
}

.minimal-input >>> .v-input__control {
  min-height: 56px;
}

.minimal-input >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  padding: 0 16px !important;
  margin: 0 !important;
  box-shadow: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:before,
.minimal-input >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:hover {
  border-color: #cbd5e1 !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-label {
  color: #64748b !important;
  font-weight: 500;
  font-size: 14px;
}

.minimal-input >>> .v-label--active {
  color: #667eea !important;
  transform: translateY(-18px) scale(0.85) !important;
}

.minimal-input >>> input {
  color: #1e293b !important;
  font-weight: 500;
  font-size: 15px;
  padding: 8px 0 !important;
}

.minimal-input >>> .v-input__prepend-inner .v-icon {
  color: #667eea !important;
  margin-right: 12px;
}

/* Remove fieldset borders */
.minimal-input >>> fieldset {
  display: none !important;
}

/* Switch Styling */
.minimal-switch >>> .v-input--switch__thumb {
  color: #667eea !important;
}

.minimal-switch >>> .v-input--switch__track {
  color: #e2e8f0 !important;
}

.minimal-switch >>> .v-input--switch.v-input--is-label-active .v-input--switch__track {
  color: #667eea !important;
}

.minimal-switch >>> .v-label {
  color: #2c3e50 !important;
  font-weight: 500;
  font-size: 15px;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
}

.cancel-btn {
  color: #64748b !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.cancel-btn:hover {
  background: rgba(100, 116, 139, 0.1) !important;
}

.save-btn {
  background: #667eea !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.save-btn:hover {
  background: #5a6fd8 !important;
}

.save-btn:before {
  background-color: transparent;
}

/* Responsive Design */
@media (max-width: 960px) {
  .filters-single-row {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .table-title-section {
    justify-content: center;
  }
  
  .filters-section {
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
}

@media (max-width: 600px) {
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-search.clean-filter,
  .filter-select.clean-filter {
    width: 100% !important;
    min-width: auto !important;
  }
  
  .filter-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .clean-btn {
    width: 100%;
  }
  
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .modern-card-title {
    padding: 16px !important;
    flex-direction: column;
    align-items: stretch;
  }
  
  .table-title {
    font-size: 18px;
    text-align: center;
    margin-bottom: 16px;
  }
}
</style>